/**
 * Pure chip-row model for collection surfaces (CB-4).
 *
 * `slugToLabel` and `compareChipLabels` are the byte-identical pair that
 * existed twice as `slugToLabel`/`compareIndustrySlugs` (palette.js and
 * add-section-modal.js) — both now import from here. `buildChipModel` is the
 * template picker's pinned-chips-plus-overflow-drawer arithmetic
 * (palette.js `showGrid`), extracted as data so CB-5a's chrome can render it
 * and CB-13 can hand palette the same model it computes today.
 */

/** 'home-services' → 'Home Services'. */
export function slugToLabel(slug) {
  return slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Order two chip keys by their human LABEL (base sensitivity, numeric-aware),
 * falling back to the raw slug so the sort is total and stable.
 */
export function compareChipLabels(a, b) {
  const labelCompare = slugToLabel(String(a || '')).localeCompare(
    slugToLabel(String(b || '')),
    undefined,
    {
      sensitivity: 'base',
      numeric: true
    }
  );
  return labelCompare || String(a || '').localeCompare(String(b || ''));
}

/**
 * The chip-row model: which keys are pinned, which overflow into the drawer,
 * and how the drawer groups.
 *
 * Donor semantics (palette.js), kept except where noted:
 *   · WHICH keys are pinned is the top `pinnedCount` by record count, ties
 *     broken by label order — the populous chips are the useful ones to show;
 *   · an `activeKey` outside the top N is FORCE-PINNED so the active filter is
 *     always visible in the row — but only when it is a real key of this
 *     record set, so a stale or synthetic active key ('all', 'favorites')
 *     cannot inject a ghost chip;
 *   · the ORDER they render in is alphabetical by label (2026-09-05, and the
 *     one deviation from the donor). Count order made the category line
 *     reshuffle every time the record set changed — the chip you reached for
 *     last time is somewhere else this time — and it put the answer to
 *     "is X in here?" nowhere in particular. Selection by count and display by
 *     label are different questions, and this is the only place either is
 *     decided, so every collection surface gets the same line;
 *   · overflow = every remaining key in LABEL order;
 *   · the drawer groups by the first letter of the LABEL, not the slug
 *     (uppercased, '#' when the label is empty), letters sorted.
 *
 * @param {Array<object>} records
 * @param {{
 *   keyOf?: (record: object) => string,
 *   keysOf?: (record: object) => string[],
 *   labelOf?: (key: string) => string,
 *   pinnedCount?: number,
 *   activeKey?: string
 * }} [opts]
 * @returns {{
 *   pinned: string[],
 *   overflow: string[],
 *   groupedOverflow: Array<{letter: string, keys: string[]}>,
 *   counts: Map<string, number>,
 *   total: number
 * }}
 */
export function buildChipModel(records, opts = {}) {
  const list = Array.isArray(records) ? records : [];
  const keyOf = typeof opts.keyOf === 'function' ? opts.keyOf : (r) => String(r?.key ?? 'other');
  // `keysOf` (CB-12b): a record that belongs to SEVERAL chips. A section
  // variant carries `industries: string[]` — one hero is both plumbing and
  // dental — so `keyOf` cannot describe it, and picking `industries[0]` would
  // silently drop the variant from every other chip it belongs to. When
  // absent, `keyOf` runs exactly as before and single-key callers (palette,
  // Add Section's own categories) are byte-identical.
  const keysOf = typeof opts.keysOf === 'function' ? opts.keysOf : null;
  const labelOf = typeof opts.labelOf === 'function' ? opts.labelOf : slugToLabel;
  const pinnedCount = Number.isInteger(opts.pinnedCount) ? opts.pinnedCount : 5;
  const activeKey = opts.activeKey;

  const counts = new Map();
  for (const record of list) {
    // A record with no keys contributes to no chip — it is not "other". The
    // total still counts it, because `total` is the record set, not the chips.
    const recordKeys = keysOf ? keysOf(record) || [] : [keyOf(record)];
    for (const key of recordKeys) counts.set(key, (counts.get(key) || 0) + 1);
  }

  const keys = [...counts.keys()].sort(compareChipLabels);
  const sortedByCount = [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || compareChipLabels(a[0], b[0]))
    .map(([k]) => k);

  const pinned = sortedByCount.slice(0, Math.max(0, pinnedCount));
  const pinnedSet = new Set(pinned);
  if (activeKey && !pinnedSet.has(activeKey) && counts.has(activeKey)) {
    pinned.push(activeKey);
    pinnedSet.add(activeKey);
  }

  // Selected by count above; READ alphabetically. The force-pinned active key
  // sorts in with the rest rather than being stranded at the end, where it read
  // as a different kind of thing than the chip beside it.
  pinned.sort(compareChipLabels);

  const overflow = keys.filter((k) => !pinnedSet.has(k));

  const groups = new Map();
  for (const key of overflow) {
    const letter = (labelOf(key)[0] || '#').toUpperCase();
    if (!groups.has(letter)) groups.set(letter, []);
    groups.get(letter).push(key);
  }
  const groupedOverflow = [...groups.keys()].sort().map((letter) => ({
    letter,
    keys: groups.get(letter)
  }));

  return { pinned, overflow, groupedOverflow, counts, total: list.length };
}
